import {type RefObject, useCallback, useState} from 'react';

import {suppressClickAfterDrag} from '../lib/pointer-drag';
import {expandFlowDragSelection} from '../lib/flow-insertion';
import type {PhotoRequirement} from '../lib/types';

interface UseFlowDragParams {
  requirements: PhotoRequirement[];
  orderedFlowRenderIds: string[];
  elemRefs: RefObject<Map<string, HTMLDivElement>>;
  /** Read-only view of the shared drag context (only `selectedIds` is needed here). */
  dragCtxRef: RefObject<{selectedIds?: string[]}>;
  groupContext: (item: PhotoRequirement) => {sentinelGroupId: string | null};
  onReorderFlow?: (
    movingIds: string[],
    target: {refId: string; placement: 'before' | 'after'},
  ) => void;
  fillMode: boolean;
  inlineEditId: string | null;
}

/**
 * Drag-to-reorder for flow (non-absolute) elements: computes the moving block
 * (expanded to whole groups / the active multi-selection), hit-tests the drop
 * position against rendered flow items across pages, and shows a live drop
 * indicator (`flowDrag`) until release. Extracted from LivePreview.
 */
export function useFlowDrag({
  requirements,
  orderedFlowRenderIds,
  elemRefs,
  dragCtxRef,
  groupContext,
  onReorderFlow,
  fillMode,
  inlineEditId,
}: UseFlowDragParams) {
  const [flowDrag, setFlowDrag] = useState<{
    idSet: Set<string>;
    drop: {refId: string; placement: 'before' | 'after'} | null;
  } | null>(null);

  const startFlowDrag = useCallback(
    (e: React.MouseEvent, item: PhotoRequirement) => {
      // Flow items never start a page marquee, regardless of whether reordering
      // is enabled — keep that invariant.
      e.stopPropagation();
      if (e.button !== 0 || !onReorderFlow || fillMode || inlineEditId === item.id) return;
      // Suppress the browser's native text selection for this gesture — without it,
      // dragging an element rubber-bands a text selection across whatever elements
      // the cursor sweeps over. (Click/double-click still fire after preventDefault.)
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      let activated = false;

      // Moving block: a multi-selection containing this item, otherwise this item
      // (or its whole group). Expanded so groups always travel together.
      const {selectedIds: curSel} = dragCtxRef.current;
      const {sentinelGroupId} = groupContext(item);
      const seeds =
        curSel && curSel.length > 1 && curSel.includes(item.id)
          ? [...curSel]
          : sentinelGroupId
            ? [sentinelGroupId]
            : [item.id];
      const movingIds = expandFlowDragSelection(requirements, seeds);
      const idSet = new Set(movingIds);

      const computeDrop = (
        clientY: number,
      ): {refId: string; placement: 'before' | 'after'} | null => {
        let last: string | null = null;
        for (const id of orderedFlowRenderIds) {
          if (idSet.has(id)) continue;
          const el = elemRefs.current.get(id);
          if (!el) continue;
          const r = el.getBoundingClientRect();
          if (clientY < r.top + r.height / 2) return {refId: id, placement: 'before'};
          last = id;
        }
        return last ? {refId: last, placement: 'after'} : null;
      };

      const onMove = (me: MouseEvent) => {
        if (!activated && Math.hypot(me.clientX - startX, me.clientY - startY) < 4) return;
        if (!activated) {
          activated = true;
          // Once dragging actually begins, lock selection off and drop any range that
          // formed before the movement threshold was crossed.
          document.body.style.userSelect = 'none';
          window.getSelection?.()?.removeAllRanges();
        }
        setFlowDrag({idSet, drop: computeDrop(me.clientY)});
      };

      const onUp = (me: MouseEvent) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        if (activated) {
          document.body.style.userSelect = '';
          const drop = computeDrop(me.clientY);
          if (drop) onReorderFlow(movingIds, drop);
          // Swallow the click that fires after a drag so it doesn't change
          // selection. The shared one, which also unhooks when no click follows
          // — this copy leaked its listener into the author's next gesture.
          suppressClickAfterDrag();
        }
        setFlowDrag(null);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [
      onReorderFlow,
      fillMode,
      inlineEditId,
      requirements,
      orderedFlowRenderIds,
      groupContext,
      dragCtxRef,
      elemRefs,
    ],
  );

  return {flowDrag, startFlowDrag};
}
